import {convertBytes} from '@wandb/weave/util';
import {create} from 'zustand';
import {devtools} from 'zustand/middleware';

import {recordRumAction} from '../../../util/datadog/datadogRum';
import type {ArtifactFileEntry} from './utils';
import {
  ARTIFACT_DOWNLOAD_FILE_COUNT_LIMIT,
  ARTIFACT_DOWNLOAD_SIZE_LIMIT,
  buildZipAndStoreInOpfs,
  downloadOpfsFile,
  OPFS_AVAILABLE,
  removeOpfsFile,
} from './utils';

let downloadAbortController: AbortController | null = null;

function abortAndClearController(reason: string) {
  downloadAbortController?.abort(new DOMException(reason, 'AbortError'));
  downloadAbortController = null;
}

export enum DownloadStateEnum {
  PENDING = 'pending',
  READY_TO_BUILD = 'ready_to_build',
  COMPRESSING = 'compressing',
  READY_TO_DOWNLOAD = 'ready_to_download',
  ERROR = 'error',
}

export type DownloadArtifactStoreState = {
  zipFileName: string;
  artifactSize: number;
  fileCount: number;
  hasReferenceFiles: boolean;
  downloadState: DownloadStateEnum;
  downloadedBytes: number;
  compressionStartedAt: number | null;
  fileHandle: FileSystemFileHandle | null;
  canDownload: boolean;
  artifactFiles: ArtifactFileEntry[] | undefined;
};

export type DownloadArtifactStoreActions = {
  init: (artifactSize: number, fileCount: number, zipFileName: string) => void;
  reset: () => void;
  setArtifactFiles: (
    files: ArtifactFileEntry[] | null,
    hasReferenceFiles?: boolean
  ) => void;

  maybeStartArtifactZipBuild: () => Promise<void>;
  cancelArtifactZipBuild: () => void;
  downloadArtifactZip: () => Promise<void>;
};

export type DownloadArtifactStore = DownloadArtifactStoreState &
  DownloadArtifactStoreActions;

const getInitialState = (
  artifactSize?: number,
  fileCount?: number,
  zipFileName?: string
): DownloadArtifactStoreState => {
  const size = artifactSize ?? 0;
  const count = fileCount ?? 0;
  return {
    zipFileName: zipFileName ?? '',
    artifactSize: size,
    fileCount: count,
    hasReferenceFiles: false,
    canDownload:
      OPFS_AVAILABLE &&
      size <= ARTIFACT_DOWNLOAD_SIZE_LIMIT &&
      count > 0 &&
      count <= ARTIFACT_DOWNLOAD_FILE_COUNT_LIMIT,
    downloadState: DownloadStateEnum.PENDING,
    downloadedBytes: 0,
    compressionStartedAt: null,
    fileHandle: null,
    artifactFiles: undefined,
  };
};

export const useDownloadArtifactStore = create<DownloadArtifactStore>()(
  devtools(
    (set, get) => ({
      ...getInitialState(),

      init: (artifactSize: number, fileCount: number, zipFileName: string) => {
        abortAndClearController('initializing store');
        set(getInitialState(artifactSize, fileCount, zipFileName));
      },

      reset: () => {
        abortAndClearController('resetting store');
        const {zipFileName} = get();
        if (zipFileName) {
          removeOpfsFile(zipFileName);
        }
        set(getInitialState());
      },

      setArtifactFiles: (files, hasReferenceFiles = false) => {
        if (files == null || files.length === 0) {
          set({
            artifactFiles: undefined,
            canDownload: false,
            downloadState: DownloadStateEnum.ERROR,
          });
          return;
        }
        if (hasReferenceFiles) {
          set({
            artifactFiles: undefined,
            hasReferenceFiles: true,
            canDownload: false,
            downloadState: DownloadStateEnum.ERROR,
          });
          return;
        }
        set({
          artifactFiles: files,
          downloadState: DownloadStateEnum.READY_TO_BUILD,
        });
      },

      // If the artifact can be downloaded, start building the zip file and store it in the OPFS.
      maybeStartArtifactZipBuild: async () => {
        const {
          artifactFiles,
          zipFileName,
          canDownload,
          downloadState,
          fileCount,
          artifactSize,
          hasReferenceFiles,
        } = get();
        recordRumAction('artifact_zip_download_attempted', {
          canDownload,
          fileCount,
          artifactSizeRaw: artifactSize,
          artifactSize: convertBytes(artifactSize),
          isReferenceArtifact: hasReferenceFiles,
          fileCountLimitExceeded:
            fileCount > ARTIFACT_DOWNLOAD_FILE_COUNT_LIMIT,
          sizeLimitExceeded: artifactSize > ARTIFACT_DOWNLOAD_SIZE_LIMIT,
        });

        if (
          !canDownload ||
          !artifactFiles?.length ||
          downloadState !== DownloadStateEnum.READY_TO_BUILD
        ) {
          return;
        }

        // Create a new abort controller to allow the user to cancel the zip build
        downloadAbortController = new AbortController();
        const signal = downloadAbortController.signal;
        set({
          downloadState: DownloadStateEnum.COMPRESSING,
          downloadedBytes: 0,
          compressionStartedAt: Date.now(),
        });

        try {
          const handle = await buildZipAndStoreInOpfs(
            artifactFiles,
            zipFileName,
            // Keep track of the number of bytes downloaded to the zip file
            delta => set(s => ({downloadedBytes: s.downloadedBytes + delta})),
            signal
          );
          set({
            fileHandle: handle,
            downloadState: DownloadStateEnum.READY_TO_DOWNLOAD,
          });
        } catch (err: unknown) {
          if (err instanceof DOMException && err.name === 'AbortError') {
            // Allow the user to try again if the abort was intentional
            set({downloadState: DownloadStateEnum.READY_TO_BUILD});
          } else {
            set({downloadState: DownloadStateEnum.ERROR});
          }
        } finally {
          abortAndClearController('done building zip');
        }
      },

      cancelArtifactZipBuild: () =>
        abortAndClearController('User cancelled zip build'),

      downloadArtifactZip: async () => {
        const {fileHandle, zipFileName, downloadState} = get();
        if (
          !fileHandle ||
          downloadState !== DownloadStateEnum.READY_TO_DOWNLOAD
        ) {
          return;
        }
        try {
          await downloadOpfsFile(fileHandle, zipFileName);
          // Don't reset the download state, let the user download again if they want to
        } catch {
          set({downloadState: DownloadStateEnum.ERROR});
        }
      },
    }),
    {name: 'DownloadArtifactStore'}
  )
);

export const useDownloadArtifactStoreInit = () =>
  useDownloadArtifactStore(s => s.init);
export const useDownloadArtifactStoreReset = () =>
  useDownloadArtifactStore(s => s.reset);

export const useDownloadArtifactStoreSetArtifactFiles = () =>
  useDownloadArtifactStore(s => s.setArtifactFiles);
export const useDownloadArtifactStoreMaybeStartArtifactZipBuild = () =>
  useDownloadArtifactStore(s => s.maybeStartArtifactZipBuild);
export const useDownloadArtifactStoreCancelArtifactZipBuild = () =>
  useDownloadArtifactStore(s => s.cancelArtifactZipBuild);
export const useDownloadArtifactStoreDownloadArtifactZip = () =>
  useDownloadArtifactStore(s => s.downloadArtifactZip);

export const useDownloadArtifactStoreCanDownload = () =>
  useDownloadArtifactStore(s => s.canDownload);
export const useDownloadArtifactStoreArtifactSize = () =>
  useDownloadArtifactStore(s => s.artifactSize);
export const useDownloadArtifactStoreFileCount = () =>
  useDownloadArtifactStore(s => s.fileCount);
export const useDownloadArtifactStoreHasReferenceFiles = () =>
  useDownloadArtifactStore(s => s.hasReferenceFiles);
export const useDownloadArtifactStoreDownloadState = () =>
  useDownloadArtifactStore(s => s.downloadState);
export const useDownloadArtifactStoreDownloadedBytes = () =>
  useDownloadArtifactStore(s => s.downloadedBytes);
export const useDownloadArtifactStoreCompressionStartedAt = (): number | null =>
  useDownloadArtifactStore(s => s.compressionStartedAt);
